import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import { privateOfficeService } from "@/services/private-office-service";
import {
  SearchPrivateOffice,
  SearchPrivateOfficesParams,
} from "@/services/private-office-service/types";
import { spaceService } from "@/services/space-service";
import { useCallback, useState } from "react";

export function useSearchPrivateOffices() {
  const [privateOffices, setPrivateOffices] =
    useState<SearchPrivateOffice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useAuth();

  const handleSearchPrivateOffices = useCallback(
    async (params: SearchPrivateOfficesParams) => {
      setIsLoading(true);
      try {
        const space =
          params.term && params.term.length > 3
            ? await spaceService.getSpaceByLocation(params.term)
            : null;

        const term = params.term || "searchType";
        const request = {
          demands: {
            ...params.demands,
            ...(space && {
              [term]: {
                ...params.demands?.[term],
                ...(space && {
                  latitude: space.lat.toString(),
                  longitude: space.lng.toString(),
                }),
              },
            }),
          },
        };

        const response = await privateOfficeService.searchPrivateOffices({
          demands: request.demands,
          spaceTypes: params.spaceTypes,
        });
        setPrivateOffices(response);

        sendTrackDataServer({
          eventName: "SEARCH_RESULTS_RENDERED",
          data: {
            user_id: userInfo.user?.id?.toString() ?? undefined,
            email: userInfo.profile?.email ?? undefined,
            has_results: response.private_rooms_returned_ids?.length ?? 0 > 0,
            results_count: response.total_results ?? 0,
            filters: {
              term: params.term || "searchType",
              ...params.demands,
            },
          },
          url: location.pathname,
          productType: "PO",
        });
      } catch (error) {
        console.error(error);
        setPrivateOffices(null);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    privateOffices,
    handleSearchPrivateOffices,
    isLoading,
    setIsLoading,
    setPrivateOffices,
  };
}
